'use client';

import React from 'react';
import Cookies from 'js-cookie';
import Modal from '@repo/ui/src/modules/UI/components/Modal';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { useLocalStorage } from '@repo/ui/src/hooks/useLocalStorage';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { getTranslationsForLocale } from '@repo/ui/src/context/Translation/getTranslation';
import { GeoLang } from '@repo/ui/src/types';
import { CountryDetails } from './_CountryDetails';

const DISMISSED_LABEL = 'country-selector-dismissed';

interface CountryModalProps {
  geo: GeoLang;
}

export const CountryModal = ({ geo }: CountryModalProps) => {
  const { push, close } = useModal(ModalID.country);
  const { t } = useTranslation();
  const [hasDismissed, setDismissed, loading] = useLocalStorage(DISMISSED_LABEL, false, {
    useSessionStorage: true,
  });

  // State to hold translations for user's detected locale
  const [userLocaleTranslation, setUserLocaleTranslation] = React.useState<
    ((key: string, values?: any, options?: any) => string) | null
  >(null);

  const usersGeo = React.useMemo(() => {
    try {
      const geoString = Cookies.get('geo');
      const usersGeo = !!geoString && (JSON.parse(geoString) as GeoLang);
      if (usersGeo && !hasDismissed && !loading && geo?.productGeo !== usersGeo.productGeo) {
        return usersGeo;
      }
    } catch {
      /* empty */
    }
  }, [loading, hasDismissed, geo]);

  // Load translations for user's detected locale
  React.useEffect(() => {
    if (usersGeo?.locale) {
      getTranslationsForLocale(usersGeo.locale).then(({ t: userT }) => {
        setUserLocaleTranslation(() => userT);
      });
    }
  }, [usersGeo?.locale]);

  React.useEffect(() => {
    if (usersGeo) {
      push();
    }
  }, [usersGeo, push]);

  // Use user's locale translation if available, otherwise fall back to current page translation
  const modalT = userLocaleTranslation || t;

  return (
    <Modal
      id={ModalID.country}
      className="tw-p-15 tw-max-w-xl tw-grid tw-gap-y-6 md:tw-gapy-y-12 tw-justify-items-center"
      onClose={() => {
        setDismissed(true);
      }}
      aria-labelledby={`${ModalID.country}-modal-title`}
      data-testId={`${ModalID.country}-modal`}
    >
      <h1
        className="tw-text-white tw-grid tw-gap-y-2 tw-text-center tra-text-web-heading-4"
        id={`${ModalID.country}-modal-title`}
      >
        <span className="tra-text-web-heading-2">
          {modalT('countryPopupContent.whereAreYou', undefined, { default: 'Grilling from' })}
        </span>
        <span>
          {modalT('countryPopupContent.shoppingFrom', undefined, { default: 'Shopping From' })}
        </span>
      </h1>
      <hr className="tw-h-1 tw-w-12 tw-bg-brand-300 tw-border-none tw-block" />

      <p className="tw-text-white tw-text-center tra-text-web-body">
        {modalT('countryPopupContent.body', undefined, {
          default: `Shop your local site for the right gear, prices, and shipping that actually makes sense`,
        })}
      </p>

      <div className="tw-grid tw-gap-y-3 md:tw-grid-cols-2 md:tw-gap-x-6">
        {usersGeo && geo && (
          <>
            <button
              aria-label="Close Modal"
              onClick={() => {
                setDismissed(true);
                close();
              }}
            >
              <CountryDetails {...geo} />
            </button>
            <a href={usersGeo.geoLoc}>
              <CountryDetails {...usersGeo} />
            </a>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CountryModal;
